import { useState, useEffect } from 'react';

export interface CollectionItem {
  id: string;
  thumbnail: string;
  prompt: string;
  style: string;
  duration: string;
  resolution: string;
  savedAt: number;
}

export interface Collection {
  id: string;
  name: string;
  emoji: string;
  color: string;
  createdAt: number;
  items: CollectionItem[];
}

interface CollectionsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  darkMode: boolean;
  pendingItem?: CollectionItem | null;
  onClearPending?: () => void;
}

const COLLECTION_COLORS = [
  { bg: 'bg-red-500', light: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', border: 'border-red-400' },
  { bg: 'bg-orange-500', light: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300', border: 'border-orange-400' },
  { bg: 'bg-amber-500', light: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', border: 'border-amber-400' },
  { bg: 'bg-green-500', light: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', border: 'border-green-400' },
  { bg: 'bg-teal-500', light: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300', border: 'border-teal-400' },
  { bg: 'bg-violet-500', light: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300', border: 'border-violet-400' },
];

const DEFAULT_EMOJIS = ['🎬', '🌟', '🎨', '🚀', '🎵', '🌊', '🔥', '✨', '🎭', '🎯'];

const STORAGE_KEY = 'ai-video-collections';

function loadCollections(): Collection[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveCollections(cols: Collection[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cols)); } catch { /* noop */ }
}

function formatDate(ts: number) {
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function CollectionsPanel({ isOpen, onClose, darkMode, pendingItem, onClearPending }: CollectionsPanelProps) {
  const [collections, setCollections] = useState<Collection[]>(loadCollections);
  const [activeCollection, setActiveCollection] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmoji, setNewEmoji] = useState('🎬');
  const [newColorIdx, setNewColorIdx] = useState(0);
  const [addingToId, setAddingToId] = useState<string | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => { saveCollections(collections); }, [collections]);

  // Auto-prompt to add when a pendingItem is passed
  useEffect(() => {
    if (pendingItem && isOpen) setAddingToId('__pick__');
  }, [pendingItem, isOpen]);

  const handleCreate = () => {
    if (!newName.trim()) return;
    const col: Collection = {
      id: `col-${Date.now()}`,
      name: newName.trim(),
      emoji: newEmoji,
      color: COLLECTION_COLORS[newColorIdx].bg,
      createdAt: Date.now(),
      items: [],
    };
    setCollections(prev => [col, ...prev]);
    setNewName('');
    setNewEmoji('🎬');
    setNewColorIdx(0);
    setShowNewForm(false);
  };

  const handleAddToCollection = (colId: string) => {
    if (!pendingItem) return;
    setCollections(prev => prev.map(c =>
      c.id === colId ? { ...c, items: [pendingItem, ...c.items.filter(i => i.id !== pendingItem.id)] } : c
    ));
    setFlashId(colId);
    setTimeout(() => setFlashId(null), 1800);
    setAddingToId(null);
    onClearPending?.();
  };

  const handleRemoveItem = (colId: string, itemId: string) => {
    setCollections(prev => prev.map(c =>
      c.id === colId ? { ...c, items: c.items.filter(i => i.id !== itemId) } : c
    ));
  };

  const handleDeleteCollection = (colId: string) => {
    setCollections(prev => prev.filter(c => c.id !== colId));
    if (activeCollection === colId) setActiveCollection(null);
    setDeleteConfirm(null);
  };

  const activeCol = collections.find(c => c.id === activeCollection);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose}></div>
      <div className={`fixed top-0 right-0 bottom-0 w-full max-w-md z-50 flex flex-col ${
        darkMode ? 'bg-gray-900 border-l border-gray-700' : 'bg-white border-l border-gray-200'
      }`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-orange-600 p-5">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-3">
              {activeCol ? (
                <button
                  onClick={() => setActiveCollection(null)}
                  className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                >
                  <i className="ri-arrow-left-line text-white text-base"></i>
                </button>
              ) : (
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-folder-3-line text-white text-base"></i>
                </div>
              )}
              <div>
                <h3 className="text-white font-bold text-base">
                  {activeCol ? activeCol.name : 'My Collections'}
                </h3>
                <p className="text-white/70 text-xs">
                  {activeCol
                    ? `${activeCol.items.length} video${activeCol.items.length !== 1 ? 's' : ''}`
                    : `${collections.length} collection${collections.length !== 1 ? 's' : ''}`
                  }
                </p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition-colors">
              <i className="ri-close-line text-white text-lg"></i>
            </button>
          </div>
        </div>

        {/* Pending item banner */}
        {pendingItem && !activeCol && (
          <div className={`flex items-center gap-3 px-5 py-3 border-b ${
            darkMode ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200'
          }`}>
            <img src={pendingItem.thumbnail} alt="" className="w-12 h-8 rounded object-cover flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-semibold ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                <i className="ri-add-circle-line mr-1"></i>Ready to save — pick a collection
              </p>
              <p className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{pendingItem.prompt.slice(0, 60)}…</p>
            </div>
            <button onClick={onClearPending} className={`text-xs ${darkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'}`}>
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {/* Collection list view */}
          {!activeCol && (
            <div className="p-5 space-y-3">
              {/* New collection button */}
              {!showNewForm ? (
                <button
                  onClick={() => setShowNewForm(true)}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 border-dashed transition-all ${
                    darkMode ? 'border-gray-700 text-gray-400 hover:border-red-700 hover:text-red-400' : 'border-gray-300 text-gray-500 hover:border-red-400 hover:text-red-500'
                  }`}
                >
                  <div className="w-9 h-9 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-add-line text-white text-base"></i>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold">New Collection</p>
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Create a named folder</p>
                  </div>
                </button>
              ) : (
                <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  <p className={`text-xs font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>New Collection</p>

                  {/* Emoji selector */}
                  <div className="mb-3">
                    <p className={`text-xs mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Icon</p>
                    <div className="relative">
                      <button
                        onClick={() => setShowEmojiPicker(p => !p)}
                        className={`px-3 py-2 rounded-lg border text-lg ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                      >
                        {newEmoji}
                      </button>
                      {showEmojiPicker && (
                        <div className={`absolute top-10 left-0 z-10 p-2 rounded-xl border shadow-lg flex flex-wrap gap-1 w-48 ${
                          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                          {DEFAULT_EMOJIS.map(e => (
                            <button
                              key={e}
                              onClick={() => { setNewEmoji(e); setShowEmojiPicker(false); }}
                              className={`w-8 h-8 text-lg rounded-lg flex items-center justify-center transition-colors ${
                                newEmoji === e
                                  ? 'bg-red-100 dark:bg-red-900/30'
                                  : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                              }`}
                            >
                              {e}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Name input */}
                  <input
                    type="text"
                    value={newName}
                    onChange={e => setNewName(e.target.value.slice(0, 40))}
                    onKeyDown={e => e.key === 'Enter' && handleCreate()}
                    placeholder="Collection name…"
                    className={`w-full px-3 py-2 rounded-lg border text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                  />

                  {/* Color picker */}
                  <div className="mb-3">
                    <p className={`text-xs mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Color</p>
                    <div className="flex gap-2">
                      {COLLECTION_COLORS.map((c, i) => (
                        <button
                          key={i}
                          onClick={() => setNewColorIdx(i)}
                          className={`w-7 h-7 rounded-full ${c.bg} transition-all ${
                            newColorIdx === i ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                          }`}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button onClick={() => setShowNewForm(false)} className={`flex-1 py-2 rounded-lg text-sm font-medium ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>Cancel</button>
                    <button onClick={handleCreate} disabled={!newName.trim()} className="flex-1 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-red-500 to-orange-600 text-white disabled:opacity-40">Create</button>
                  </div>
                </div>
              )}

              {/* Collection cards */}
              {collections.length === 0 && !showNewForm && (
                <div className="text-center py-12">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    <i className="ri-folder-3-line text-2xl text-gray-400"></i>
                  </div>
                  <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>No collections yet</p>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Create your first folder above</p>
                </div>
              )}

              {collections.map(col => {
                const colorMeta = COLLECTION_COLORS.find(c => c.bg === col.color) || COLLECTION_COLORS[0];
                const isFlashing = flashId === col.id;
                const canAdd = pendingItem && addingToId === '__pick__';
                return (
                  <div
                    key={col.id}
                    className={`rounded-xl border-2 transition-all overflow-hidden ${
                      isFlashing ? 'border-green-400' : canAdd ? 'border-red-300 dark:border-red-700' : darkMode ? 'border-gray-700' : 'border-gray-200'
                    } ${canAdd ? 'cursor-pointer' : ''}`}
                    onClick={canAdd ? () => handleAddToCollection(col.id) : undefined}
                  >
                    {/* Collection header */}
                    <div className={`flex items-center gap-3 px-4 py-3 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                      <div className={`w-10 h-10 ${col.color} rounded-xl flex items-center justify-center text-xl flex-shrink-0`}>
                        {col.emoji}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{col.name}</p>
                        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {col.items.length} video{col.items.length !== 1 ? 's' : ''} · {formatDate(col.createdAt)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {canAdd && (
                          <span className={`text-xs px-2 py-1 rounded-full font-semibold ${colorMeta.light}`}>
                            <i className="ri-add-line mr-0.5"></i>Add here
                          </span>
                        )}
                        {isFlashing && (
                          <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full font-semibold">
                            <i className="ri-check-line mr-0.5"></i>Saved!
                          </span>
                        )}
                        {!canAdd && (
                          <>
                            <button
                              onClick={e => { e.stopPropagation(); setActiveCollection(col.id); }}
                              className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-white' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-900'}`}
                              title="Open collection"
                            >
                              <i className="ri-folder-open-line text-base"></i>
                            </button>
                            {deleteConfirm === col.id ? (
                              <button
                                onClick={e => { e.stopPropagation(); handleDeleteCollection(col.id); }}
                                className="px-2 py-1 bg-red-500 text-white text-xs rounded-lg font-semibold"
                              >
                                Delete?
                              </button>
                            ) : (
                              <button
                                onClick={e => { e.stopPropagation(); setDeleteConfirm(col.id); setTimeout(() => setDeleteConfirm(null), 3000); }}
                                className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-gray-500 hover:bg-gray-700 hover:text-red-400' : 'text-gray-400 hover:bg-gray-200 hover:text-red-500'}`}
                                title="Delete collection"
                              >
                                <i className="ri-delete-bin-line text-base"></i>
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Thumbnail strip */}
                    {col.items.length > 0 && (
                      <div className="flex gap-1 px-3 py-2">
                        {col.items.slice(0, 5).map(item => (
                          <div key={item.id} className="flex-1 aspect-video rounded-lg overflow-hidden min-w-0">
                            <img src={item.thumbnail} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                        {col.items.length > 5 && (
                          <div className={`flex-shrink-0 w-10 aspect-video rounded-lg flex items-center justify-center text-xs font-bold ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                            +{col.items.length - 5}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Collection detail view */}
          {activeCol && (
            <div className="p-5">
              {activeCol.items.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-5xl mb-4">{activeCol.emoji}</div>
                  <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>No videos yet</p>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Generate a video and click &ldquo;Save to Collection&rdquo;</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {activeCol.items.map(item => (
                    <div key={item.id} className={`rounded-xl overflow-hidden border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="relative group">
                        <img src={item.thumbnail} alt={item.prompt} className="w-full h-40 object-cover" />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleRemoveItem(activeCol.id, item.id)}
                            className="px-3 py-2 bg-red-500 text-white text-xs font-semibold rounded-lg"
                          >
                            <i className="ri-delete-bin-line mr-1"></i>Remove
                          </button>
                        </div>
                        <div className="absolute top-2 right-2 flex gap-1">
                          <span className={`px-2 py-0.5 bg-black/50 text-white text-xs rounded-lg backdrop-blur-sm`}>{item.style}</span>
                          <span className={`px-2 py-0.5 bg-black/50 text-white text-xs rounded-lg backdrop-blur-sm`}>{item.duration}</span>
                        </div>
                      </div>
                      <div className="p-3">
                        <p className={`text-xs line-clamp-2 mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{item.prompt}</p>
                        <p className={`text-[11px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{formatDate(item.savedAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
